import { DocumentPreview, DocumentPreviewContent, DocumentType } from '../types';

interface DriveServiceResponse {
  content?: Array<{ text: string }>;
  isError?: boolean;
}

class DocumentService {
  private baseUrl = import.meta.env.VITE_API_BASE_URL;

  // Parse Google Drive URLs to extract file IDs
  private extractFileId(url: string): string | null {
    const patterns = [
      /\/file\/d\/([a-zA-Z0-9-_]+)/,
      /id=([a-zA-Z0-9-_]+)/,
      /\/document\/d\/([a-zA-Z0-9-_]+)/,
      /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/,
      /\/presentation\/d\/([a-zA-Z0-9-_]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }

  // Determine document type from MIME type or file extension
  private getDocumentType(mimeType: string, fileName: string): DocumentType {
    if (mimeType.includes('application/vnd.google-apps.document')) {
      return 'google_doc';
    }
    if (mimeType.includes('application/vnd.google-apps.spreadsheet')) {
      return 'google_sheet';
    }
    if (mimeType.includes('application/vnd.google-apps.presentation')) {
      return 'google_slides';
    }
    if (mimeType.includes('application/pdf')) {
      return 'pdf';
    }
    if (mimeType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') ||
        mimeType.includes('application/msword')) {
      return 'word_doc';
    }
    if (mimeType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
        mimeType.includes('application/vnd.ms-excel')) {
      return 'excel';
    }
    if (mimeType.includes('application/vnd.openxmlformats-officedocument.presentationml.presentation') ||
        mimeType.includes('application/vnd.ms-powerpoint')) {
      return 'powerpoint';
    }
    if (mimeType.startsWith('image/')) {
      return 'image';
    }
    if (mimeType.startsWith('text/')) {
      return 'text';
    }
    return 'other';
  }

  // Get document preview from Google Drive
  async getDocumentPreview(fileId: string): Promise<DocumentPreview | null> {
    try {
      console.log('🔍 Getting document preview for fileId:', fileId);
      
      // First get file metadata
      const metadataResponse = await fetch(`${this.baseUrl}/api/chat/drive/preview/${fileId}`, {
        credentials: 'include'
      });

      console.log('📡 Metadata response status:', metadataResponse.status);

      if (!metadataResponse.ok) {
        const errorText = await metadataResponse.text();
        console.error('❌ Failed to fetch document metadata:', metadataResponse.status, errorText);
        throw new Error(`Failed to fetch document metadata: ${metadataResponse.status} ${errorText}`);
      }

      const metadata = await metadataResponse.json();
      console.log('📄 File metadata:', metadata);
      
      const documentType = this.getDocumentType(metadata.mimeType, metadata.name);
      console.log('📝 Document type determined:', documentType);
      
      let previewContent: DocumentPreviewContent | undefined;

      // Get appropriate preview content based on document type
      switch (documentType) {
        case 'google_sheet':
        case 'excel':
          console.log('📊 Getting spreadsheet preview...');
          previewContent = await this.getSpreadsheetPreview(fileId);
          break;
        case 'google_doc':
        case 'word_doc':
        case 'text':
          console.log('📝 Getting text preview...');
          previewContent = await this.getTextPreview(fileId);
          break;
        case 'pdf':
          console.log('📄 Getting PDF preview...');
          previewContent = await this.getPdfPreview(fileId);
          break;
        default:
          console.log('📁 Getting generic preview...');
          previewContent = await this.getGenericPreview(fileId);
      }

      const preview: DocumentPreview = {
        id: fileId,
        name: metadata.name,
        type: documentType,
        mimeType: metadata.mimeType,
        url: metadata.webViewLink,
        fileId: fileId,
        size: metadata.size ? parseInt(metadata.size) : undefined,
        modifiedTime: metadata.modifiedTime,
        previewContent,
        thumbnailUrl: metadata.thumbnailLink,
        canEdit: metadata.capabilities?.canEdit,
        owner: metadata.owners?.[0]?.displayName
      };

      console.log('✅ Document preview created:', preview);
      return preview;
    } catch (error) {
      console.error('❌ Failed to get document preview:', error);
      return null;
    }
  }

  // Get spreadsheet preview
  private async getSpreadsheetPreview(fileId: string): Promise<DocumentPreviewContent> {
    try {
      const response = await fetch(`${this.baseUrl}/api/chat/drive/call-tool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: 'list_gdrive_sheets',
          arguments: { file_id: fileId }
        })
      });

      const result = await response.json() as DriveServiceResponse;
      
      if (result.isError || !result.content?.[0]?.text) {
        return { type: 'spreadsheet', data: {} };
      }

      const sheetData = result.content[0].text;
      const sheetNames = sheetData.split('\n')
        .filter(line => line.trim())
        .map(line => line.replace(/^\d+\.\s*/, ''));

      return {
        type: 'spreadsheet',
        data: { sheets: sheetNames },
        sheetNames: sheetNames,
        summary: `${sheetNames.length} sheets`
      };
    } catch (error) {
      console.error('Failed to get spreadsheet preview:', error);
      return { type: 'spreadsheet', data: {} };
    }
  }

  // Get text preview
  private async getTextPreview(fileId: string): Promise<DocumentPreviewContent> {
    try {
      const response = await fetch(`${this.baseUrl}/api/chat/drive/call-tool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: 'preview_gdrive_file',
          arguments: { file_id: fileId }
        })
      });

      const result = await response.json() as DriveServiceResponse;
      
      if (result.isError || !result.content?.[0]?.text) {
        return { type: 'text', data: {} };
      }

      const fullText = result.content[0].text;
      const preview = fullText.substring(0, 500); // First 500 characters

      return {
        type: 'text',
        data: { fullText },
        preview: preview,
        summary: `${fullText.length} characters`
      };
    } catch (error) {
      console.error('Failed to get text preview:', error);
      return { type: 'text', data: {} };
    }
  }

  // Get PDF preview
  private async getPdfPreview(fileId: string): Promise<DocumentPreviewContent> {
    try {
      const response = await fetch(`${this.baseUrl}/api/chat/drive/call-tool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: 'convert_pdf_to_text',
          arguments: { file_id: fileId }
        })
      });

      const result = await response.json() as DriveServiceResponse;
      
      if (result.isError || !result.content?.[0]?.text) {
        return { type: 'pdf', data: {} };
      }

      const fullText = result.content[0].text;
      const preview = fullText.substring(0, 300); // First 300 characters
      const pageCount = (fullText.match(/\f/g) || []).length + 1; // Rough page count

      return {
        type: 'pdf',
        data: { fullText },
        preview: preview,
        pageCount: pageCount,
        summary: `${pageCount} pages`
      };
    } catch (error) {
      console.error('Failed to get PDF preview:', error);
      return { type: 'pdf', data: {} };
    }
  }

  // Get generic preview
  private async getGenericPreview(fileId: string): Promise<DocumentPreviewContent> {
    try {
      const response = await fetch(`${this.baseUrl}/api/chat/drive/call-tool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: 'preview_gdrive_file',
          arguments: { file_id: fileId }
        })
      });

      const result = await response.json() as DriveServiceResponse;
      
      if (result.isError || !result.content?.[0]?.text) {
        return { type: 'other', data: {} };
      }

      const content = result.content[0].text;
      const preview = content.substring(0, 200);

      return {
        type: 'other',
        data: { content },
        preview: preview,
        summary: 'File preview available'
      };
    } catch (error) {
      console.error('Failed to get generic preview:', error);
      return { type: 'other', data: {} };
    }
  }

  // Parse documents from message content and steps
  async parseDocumentsFromMessage(content: string): Promise<DocumentPreview[]> {
    console.log('🔍 Parsing documents from message content:', content.substring(0, 100) + '...');
    
    const documents: DocumentPreview[] = [];
    
    // Google Drive URL patterns
    const driveUrlPatterns = [
      /https:\/\/docs\.google\.com\/document\/d\/([a-zA-Z0-9-_]+)/g,
      /https:\/\/docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/g,
      /https:\/\/docs\.google\.com\/presentation\/d\/([a-zA-Z0-9-_]+)/g,
      /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9-_]+)/g
    ];

    for (const pattern of driveUrlPatterns) {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const fileId = match[1];
        console.log('🔗 Found URL with fileId:', fileId);
        const preview = await this.getDocumentPreview(fileId);
        if (preview) {
          documents.push(preview);
        }
      }
    }

    // Only parse actual document URLs - no longer create fake documents from text patterns
    console.log('✅ Document parsing complete - only real URLs are processed');

    console.log('📄 Documents found from URLs:', documents.length);
    return documents;
  }

  // Search for documents in message steps (agent steps that found documents)
  parseDocumentsFromSteps(steps?: any[]): DocumentPreview[] {
    const documents: DocumentPreview[] = [];
    
    if (!steps) {
      console.log('🚫 No steps provided for document parsing');
      return documents;
    }

    console.log(`🔍 Parsing documents from ${steps.length} steps`);

    for (const step of steps) {
      // Only check steps that explicitly searched for or accessed documents
      if (!step.action || !(
        step.action.includes('search_gdrive') || 
        step.action.includes('gdrive_search') ||
        step.action.includes('read_gdrive') ||
        step.action === 'search_gdrive_files' ||
        step.action === 'read_gdrive_file' ||
        step.action === 'get_gdrive_file'
      )) {
        continue; // Skip non-document related steps
      }

      const observation = step.observation || '';
      
      // Only look for explicit Google Drive file IDs with strong context
      const driveIdPatterns = [
        // Pattern: "Google Drive ID is [fileId]" or "file ID is [fileId]"
        /(?:Google Drive |file )?ID is ([a-zA-Z0-9-_]{25,})/gi,
        // Pattern: "file ID: [fileId]" 
        /file ID:\s*([a-zA-Z0-9-_]{25,})/gi,
        // Pattern: "document ID [fileId]"
        /document ID:?\s*([a-zA-Z0-9-_]{25,})/gi
      ];

      for (const pattern of driveIdPatterns) {
        let match;
        while ((match = pattern.exec(observation)) !== null) {
          const fileId = match[1];
          
          // Strict validation for Google Drive file ID format
          if (fileId.length >= 25 && fileId.length <= 50 && /^[a-zA-Z0-9-_]+$/.test(fileId)) {
            
            // Skip if we already found this document
            if (documents.find(doc => doc.fileId === fileId)) {
              console.log('🔄 Skipping duplicate file ID:', fileId);
              continue;
            }

            // Must explicitly mention it's a document, file, or have document-related context
            if (!observation.toLowerCase().includes('document') && 
                !observation.toLowerCase().includes('file') &&
                !observation.toLowerCase().includes('gdrive') &&
                !observation.toLowerCase().includes('drive')) {
              console.log('🚫 Skipping - no document context for:', fileId);
              continue;
            }

            console.log('🎯 Found Google Drive document in step:', { 
              action: step.action, 
              fileId,
              observationSnippet: observation.substring(0, 200) + '...'
            });

            // Try to extract the document name
            const namePatterns = [
              /(?:document|file) (?:is )?(?:called |named )?['"]([^'"]+)['"]/gi,
              /['"]([^'"]+)['"] (?:document|file)/gi,
              /(?:title|name):\s*['"]([^'"]+)['"]/gi
            ];

            let documentName = 'Document'; 
            for (const namePattern of namePatterns) {
              const nameMatch = namePattern.exec(observation);
              if (nameMatch && nameMatch[1] && nameMatch[1].trim().length > 2) {
                documentName = nameMatch[1].trim();
                console.log('📝 Extracted document name:', documentName);
                break;
              }
            }

            // Create document preview only for verified documents
            documents.push({
              id: fileId,
              name: documentName,
              type: 'google_doc',
              mimeType: 'application/vnd.google-apps.document',
              fileId: fileId,
              url: this.getGoogleDriveUrl(fileId, 'application/vnd.google-apps.document'),
              previewContent: {
                type: 'text',
                data: { source: 'agent_result' },
                preview: observation.substring(0, 300),
                summary: `Document accessed from Google Drive`
              }
            });
          }
        }
      }

      // Then check for traditional drive search results (keep existing logic)
      if (step.action && (
        step.action.includes('search_gdrive') ||
        step.action.includes('gdrive_search') ||
        step.action.includes('read_gdrive') ||
        step.action === 'search_gdrive_files' ||
        step.action === 'read_gdrive_file' ||
        step.action === 'gdrive_search_advanced' ||
        step.action.includes('drive')
      )) {
        // Parse file information from step observation
        console.log('🔍 Parsing drive step:', step.action);
        console.log('📋 Step observation:', observation.substring(0, 300) + '...');
        
        // Multiple patterns to match different result formats
        const patterns = [
          // Standard format: "filename (mimeType) - ID: fileId"
          /^(.+?) \((.+?)\) - ID: (.+)$/gm,
          // Advanced format: "filename (mimeType, size) - ID: fileId"
          /^(.+?) \((.+?), .+?\) - ID: (.+)$/gm,
          // Alternative format: "filename - ID: fileId (mimeType)"
          /^(.+?) - ID: (.+?) \((.+?)\)$/gm,
        ];
        
        let foundAny = false;
        for (const pattern of patterns) {
          let match;
          while ((match = pattern.exec(observation)) !== null) {
            foundAny = true;
            const [, name, mimeTypeOrId, idOrMimeType] = match;
            
            // Determine which is the file ID and which is the mime type
            let fileName = name.trim();
            let mimeType = '';
            let fileId = '';
            
            // Check if the pattern matched correctly
            if (match[3] && match[3].length > 10) {
              // Standard format: name, mimeType, fileId
              mimeType = mimeTypeOrId;
              fileId = idOrMimeType;
            } else if (match[2] && match[2].length > 10) {
              // Alternative format: name, fileId, mimeType
              fileId = mimeTypeOrId;
              mimeType = idOrMimeType;
            }
            
            if (fileName && fileId && fileId.length > 10) {
              const documentType = this.getDocumentType(mimeType, fileName);
              
              console.log('✅ Found document in drive step:', { fileName, mimeType, fileId, documentType });
              
              documents.push({
                id: fileId,
                name: fileName,
                type: documentType,
                mimeType: mimeType,
                fileId: fileId,
                url: this.getGoogleDriveUrl(fileId, mimeType)
              });
            }
          }
        }
        
        if (!foundAny) {
          console.log('⚠️ No documents found in step observation using standard patterns');
          console.log('📝 Full observation for debugging:', observation);
        }
        
        // Also try to extract file IDs from any Google Drive URLs in the observation
        const urlPattern = /https:\/\/(?:docs\.google\.com|drive\.google\.com)\/(?:document|spreadsheets|presentation|file)\/d\/([a-zA-Z0-9-_]+)/g;
        let urlMatch;
        while ((urlMatch = urlPattern.exec(observation)) !== null) {
          const fileId = urlMatch[1];
          if (!documents.find(doc => doc.fileId === fileId)) {
            console.log('🔗 Found document URL:', urlMatch[0]);
            // Create a basic document entry for URLs found without full metadata
            documents.push({
              id: fileId,
              name: 'Document from URL',
              type: 'other',
              mimeType: 'unknown',
              fileId: fileId,
              url: urlMatch[0]
            });
          }
        }
      } else {
        console.log(`⏭️ Skipping step with action: "${step.action}" (not a drive action)`);
      }
    }

    console.log('📊 Total documents parsed from steps:', documents.length);
    return documents;
  }

  // Helper to generate Google Drive URLs based on file type
  private getGoogleDriveUrl(fileId: string, mimeType: string): string {
    if (mimeType.includes('application/vnd.google-apps.document')) {
      return `https://docs.google.com/document/d/${fileId}/edit`;
    }
    if (mimeType.includes('application/vnd.google-apps.spreadsheet')) {
      return `https://docs.google.com/spreadsheets/d/${fileId}/edit`;
    }
    if (mimeType.includes('application/vnd.google-apps.presentation')) {
      return `https://docs.google.com/presentation/d/${fileId}/edit`;
    }
    return `https://drive.google.com/file/d/${fileId}/view`;
  }
}

export const documentService = new DocumentService();
